import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LazyLoadMeta } from 'primeng/api';
import { ColumnConfig, EntityDefinition } from '../types/entity-definition.interface';
import { GenericEntity } from '../types/generic-entity.interface';
import { PAGINATION_CONSTANTS } from '../constants/pagination.constants';
import {
  DATE_BOUND_PATTERN,
  formatDateBound,
  formatDateDDMMYYYY,
  parseFlexibleDate,
  parseRangeBound,
} from '../utils/date-range-filter.utils';

export interface ParsedFilter {
  value: unknown;
  matchMode: string;
}

export type ParsedFilters = Record<string, ParsedFilter>;

export interface TableEvent {
  first: number;
  rows: number;
  sortField: string;
  sortOrder: number;
  filters: ParsedFilters;
}

interface FilterValueLike {
  value?: unknown;
  matchMode?: string;
}
type FiltersLike = Record<string, FilterValueLike | FilterValueLike[] | undefined>;

export interface UrlQueryParams {
  page: number | null;
  pageSize: number | null;
  sortField: string | null;
  sortOrder: string | null;
  filters: string | null;
}

const DEFAULT_PAGE_SIZE = PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE;
const DATE_DDMMYYYY_RE = /^(\d{2})\.(\d{2})\.(\d{4})$/;
const DATE_RANGE_RE = new RegExp(`^(${DATE_BOUND_PATTERN})?-(${DATE_BOUND_PATTERN})?$`);
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;
const STATE_PARAM_KEYS = ['page', 'pageSize', 'sortField', 'sortOrder', 'filters'] as const;

function readParam(queryParams: Params, key: string): string | undefined {
  const raw: unknown = queryParams[key];
  return typeof raw === 'string' ? raw : undefined;
}

function hydrateValue(value: string): unknown {
  // A scalar date filter matches a day as text, so it keeps the day it names.
  if (DATE_DDMMYYYY_RE.test(value)) return parseFlexibleDate(value) ?? value;
  const rangeMatch = value.match(DATE_RANGE_RE);
  if (rangeMatch) {
    return [parseRangeBound(rangeMatch[1], 'from'), parseRangeBound(rangeMatch[2], 'to')];
  }
  if (ISO_DATETIME_RE.test(value)) {
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date;
  }
  if (value.includes(',')) return value.split(',');
  return value;
}

function serializeValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return formatDateDDMMYYYY(value);
  if (Array.isArray(value)) {
    const allDateOrNull = value.every(v => v === null || v instanceof Date);
    if (allDateOrNull && value.length >= 1 && value.length <= 2) {
      const from = value[0] instanceof Date ? formatDateBound(value[0], 'from') : '';
      const to = value[1] instanceof Date ? formatDateBound(value[1], 'to') : '';
      const combined = `${from}-${to}`;
      return combined === '-' ? null : combined;
    }
    if (value.length === 0) return null;
    return value.map(v => (v instanceof Date ? formatDateDDMMYYYY(v) : String(v))).join(',');
  }
  return String(value);
}

function encodeFilters(filters: Record<string, unknown>): string | null {
  const segments: string[] = [];
  Object.entries(filters).forEach(([key, value]) => {
    const serialized = serializeValue(value);
    if (serialized !== null) segments.push(`${key}:${serialized}`);
  });
  return segments.length > 0 ? segments.join(';') : null;
}

const INTEGER_ID_RE = /^-?\d+$/;

// The compact URL encoding is untyped text: numeric option ids come back as strings and a
// single-element multiselect selection comes back as a scalar. PrimeNG's select/multiselect
// widgets strict-compare against their typed option values, so restore the exact shape a live
// widget interaction produces (arrays of typed ids for multiselect); non-numeric values like
// the '__EMPTY__' location sentinel pass through untouched.
function coerceOptionId(value: unknown): unknown {
  return typeof value === 'string' && INTEGER_ID_RE.test(value) ? Number(value) : value;
}

function reshapeOptionValue(value: unknown, filterable: ColumnConfig['filterable']): unknown {
  if (filterable === 'multiselect') return (Array.isArray(value) ? value : [value]).map(coerceOptionId);
  if (filterable === 'select') return coerceOptionId(value);
  return value;
}

function decodeFilters(raw: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  raw.split(';').forEach(segment => {
    const colonIdx = segment.indexOf(':');
    if (colonIdx <= 0) return;
    const key = segment.slice(0, colonIdx);
    const value = segment.slice(colonIdx + 1);
    result[key] = hydrateValue(value);
  });
  return result;
}

@Injectable({
  providedIn: 'root',
})
export class PaginatedUrlSyncService {
  parseFiltersFromUrl<T extends GenericEntity>(queryParams: Params, entityDefinition: EntityDefinition<T>): ParsedFilters | undefined {
    const rawFilters = readParam(queryParams, 'filters');
    if (!rawFilters) return undefined;
    const filterValues = rawFilters.trimStart().startsWith('{') ? this.parseLegacyJsonFilters(rawFilters) : decodeFilters(rawFilters);
    if (!filterValues) return undefined;
    const columns = entityDefinition.list.columns;
    return Object.fromEntries(
      Object.entries(filterValues).map(([key, value]) => {
        const column = columns.find(c => (c.filterField ?? c.field) === key);
        const isDateColumn = column?.filterType === 'datetime' || column?.template === 'datetime' || column?.template === 'date';
        const matchMode = isDateColumn
          ? 'dateContains'
          : column?.filterable === 'multiselect'
            ? 'in'
            : column?.filterable === 'select'
              ? 'equals'
              : 'contains';
        return [key, { matchMode, value: reshapeOptionValue(value, column?.filterable) }];
      }),
    );
  }

  private parseLegacyJsonFilters(raw: string): Record<string, unknown> | undefined {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const result: Record<string, unknown> = {};
      Object.entries(parsed).forEach(([key, value]) => {
        if (typeof value === 'string') {
          result[key] = hydrateValue(value);
        } else if (Array.isArray(value)) {
          result[key] = (value as unknown[]).map(v => (typeof v === 'string' ? hydrateValue(v) : v));
        } else {
          result[key] = value;
        }
      });
      return result;
    } catch {
      return undefined;
    }
  }

  buildTableEventFromUrl<T extends GenericEntity>(queryParams: Params, entityDefinition: EntityDefinition<T>): TableEvent {
    const rawPage = readParam(queryParams, 'page');
    const rawPageSize = readParam(queryParams, 'pageSize');
    const rawSortField = readParam(queryParams, 'sortField');
    const rawSortOrder = readParam(queryParams, 'sortOrder');

    const page = rawPage ? parseInt(rawPage, 10) : 1;
    const pageSize = rawPageSize ? parseInt(rawPageSize, 10) : DEFAULT_PAGE_SIZE;
    const sortField = rawSortField ?? entityDefinition.defaultSortField ?? '';
    const sortOrder = rawSortOrder ? (rawSortOrder === 'asc' ? 1 : -1) : (entityDefinition.defaultSortOrder ?? 1);

    const filters = this.parseFiltersFromUrl(queryParams, entityDefinition) ?? {};

    return {
      first: (page - 1) * pageSize,
      rows: pageSize,
      sortField,
      sortOrder,
      filters,
    };
  }

  buildQueryParamsFromEvent<T extends GenericEntity>(event: LazyLoadMeta, entityDefinition: EntityDefinition<T>): UrlQueryParams {
    const rawSortField = Array.isArray(event.sortField) ? event.sortField[0] : event.sortField;
    const page = Math.floor((event.first ?? 0) / (event.rows ?? DEFAULT_PAGE_SIZE)) + 1;
    const pageSize = event.rows ?? DEFAULT_PAGE_SIZE;
    const sortField = rawSortField ?? entityDefinition.defaultSortField ?? '';
    const sortOrder = (event.sortOrder ?? entityDefinition.defaultSortOrder ?? 1) === 1 ? 'asc' : 'desc';

    const isDefaultSort =
      sortField === (entityDefinition.defaultSortField ?? '') &&
      sortOrder === ((entityDefinition.defaultSortOrder ?? 1) === 1 ? 'asc' : 'desc');

    const queryParams: UrlQueryParams = {
      page: page > 1 ? page : null,
      pageSize: pageSize !== DEFAULT_PAGE_SIZE ? pageSize : null,
      sortField: isDefaultSort ? null : sortField,
      sortOrder: isDefaultSort ? null : sortOrder,
      filters: null,
    };

    if (event.filters && Object.keys(event.filters).length > 0) {
      const activeFilters = this.extractActiveFilters(event.filters);
      queryParams.filters = encodeFilters(activeFilters);
    }

    return queryParams;
  }

  extractActiveFilters(filters: FiltersLike): Record<string, unknown> {
    const activeFilters: Record<string, unknown> = {};
    Object.entries(filters).forEach(([key, filter]) => {
      const filterMeta = Array.isArray(filter) ? filter[0] : filter;
      if (filterMeta?.value !== null && filterMeta?.value !== undefined && filterMeta?.value !== '') {
        activeFilters[key] = filterMeta.value;
      }
    });
    return activeFilters;
  }

  async navigateWithParams(router: Router, route: ActivatedRoute, queryParams: UrlQueryParams): Promise<boolean> {
    return router.navigate([], {
      relativeTo: route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // Grid state (filters/sort/page) lives primarily in the URL for shareable/bookmarkable
  // links, but a bare routerLink back to the list (breadcrumb, sidebar) carries no query
  // params and drops it. Mirror the last applied state into sessionStorage so those hard
  // navigations can restore it, the same way PrimeNG's own stateKey does for other grids.
  saveState(stateKey: string, queryParams: UrlQueryParams): void {
    try {
      sessionStorage.setItem(this.storageKey(stateKey), JSON.stringify(queryParams));
    } catch {
      // Storage unavailable (quota, private browsing) — persistence is best-effort.
    }
  }

  private restoreState(stateKey: string): UrlQueryParams | null {
    try {
      const raw = sessionStorage.getItem(this.storageKey(stateKey));
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      return this.isUrlQueryParams(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  // Scopes a grid's session-restore key by asset so a saved filter/sort doesn't leak
  // into another ouvrage's grid when the user switches assets.
  buildStateKey(prefix: string, assetId: number): string {
    return `${prefix}-${assetId}`;
  }

  // sessionStorage content isn't trusted: a stale shape from a previous app version, or
  // manual tampering, must not flow through as literal "undefined"/"[object Object]" strings
  // into the URL or the API call.
  private isUrlQueryParams(value: unknown): value is UrlQueryParams {
    if (typeof value !== 'object' || value === null) return false;
    const v = value as Record<string, unknown>;
    return (
      (v['page'] === null || typeof v['page'] === 'number') &&
      (v['pageSize'] === null || typeof v['pageSize'] === 'number') &&
      (v['sortField'] === null || typeof v['sortField'] === 'string') &&
      (v['sortOrder'] === null || typeof v['sortOrder'] === 'string') &&
      (v['filters'] === null || typeof v['filters'] === 'string')
    );
  }

  private storageKey(stateKey: string): string {
    return `ces-grid-state:${stateKey}`;
  }

  private hasStateParams(queryParams: Params): boolean {
    return STATE_PARAM_KEYS.some(key => readParam(queryParams, key) !== undefined);
  }

  private toParams(query: UrlQueryParams): Params {
    const params: Params = {};
    if (query.page !== null) params['page'] = String(query.page);
    if (query.pageSize !== null) params['pageSize'] = String(query.pageSize);
    if (query.sortField !== null) params['sortField'] = query.sortField;
    if (query.sortOrder !== null) params['sortOrder'] = query.sortOrder;
    if (query.filters !== null) params['filters'] = query.filters;
    return params;
  }

  // Synchronous resolution of the params a list component should treat as "the URL" on entry:
  // the real URL if it already carries grid state, otherwise a session-restored snapshot. Must
  // stay synchronous and be consumed before the grid's first render — PrimeNG's Table only
  // reliably reflects its `filters` Input into the rendered filter row on the FIRST change
  // batch; a later async update lands but never re-paints the row (confirmed against
  // node_modules/primeng Table#ngOnChanges, which doesn't special-case `filters`). A bare
  // routerLink back to the list (breadcrumb, sidebar) carries no query params, so without this
  // fallback the restored state would silently never reach the header inputs.
  resolveState(route: ActivatedRoute, stateKey: string): { queryParams: Params; toSync: UrlQueryParams | null } {
    const current = route.snapshot.queryParams;
    if (this.hasStateParams(current)) return { queryParams: current, toSync: null };
    const saved = this.restoreState(stateKey);
    if (!saved) return { queryParams: current, toSync: null };
    return { queryParams: this.toParams(saved), toSync: saved };
  }

  // Reflects a session-restored state into the browser URL for bookmarkability, without
  // blocking filter/data correctness on it. Best-effort: a failed navigate (e.g. cancelled by
  // a concurrent navigation) must not surface as an unhandled rejection.
  syncUrlInBackground(router: Router, route: ActivatedRoute, toSync: UrlQueryParams, onSettle: () => void): void {
    void this.navigateWithParams(router, route, toSync)
      .catch(() => {
        // Ignored — URL sync is a nicety, not a source of truth for the current load.
      })
      .finally(onSettle);
  }
}
